import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { Card, CardTitle, KpiCard, TableHeader, TableCell, Badge } from '../UI'
import ScheduleModal from '../Modals/ScheduleModal'
import NewRunModal from '../Modals/NewRunModal'
import {
  Calendar,
  Play,
  Repeat,
  TrendingUp,
  BarChart2,
  Clock,
  ChevronLeft,
  ChevronRight,
  Download,
  ArrowLeft,
} from 'lucide-react'
import styles from '../../_css/Simulation.module.css'
import NextButton from './NextButton'
import { compareSimulationOverTime, getAllRuns, getRunById, removeSchedule, scheduleRun, triggerReRun } from '../../requests'
import { notifyError, notifySuccess } from '../../utils/helper'
import { useSelector } from 'react-redux'
import { LLM_PLATFORMS } from './constants'

const PER_PAGE = 5

const BAR_COLORS = ['#ef4444', '#f59e0b', '#a855f7', '#3b82f6', '#22c55e', '#10b981', '#6366f1']

const emptyPagination = {
  total: 0,
  totalPages: 0,
  currentPage: 1,
  perPage: PER_PAGE,
  hasNextPage: false,
  hasPrevPage: false,
  nextPage: null,
  prevPage: null,
}

const LEGEND_ITEMS = [
  // { label: 'Overall', color: '#22c55e' },
  // { label: 'ChatGPT', color: '#10a37f' },
  // { label: 'Gemini', color: '#4285f4' },
  // { label: 'Claude', color: '#d4a574' },
  // { label: 'Perplexity', color: '#7c3aed' },
]

function formatRunDate(iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return String(iso)
  }
}

function formatDuration(created, updated) {
  if (!created || !updated) return '—'
  const ms = new Date(updated) - new Date(created)
  if (!Number.isFinite(ms) || ms < 0) return '—'
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  const rs = s % 60
  if (m > 0) return `${m}m ${rs}s`
  return `${rs}s`
}

function formatDurationMs(ms) {
  if (ms == null || !Number.isFinite(Number(ms))) return '—'
  const s = Math.floor(Number(ms) / 1000)
  const m = Math.floor(s / 60)
  const rs = s % 60
  if (m > 0) return `${m}m ${rs}s`
  return `${rs}s`
}

function platformLabel(id) {
  if (!id) return null
  return LLM_PLATFORMS.find((p) => p.id === id)?.label || id
}

function platformsFromRun(run) {
  const platforms = run?.results?.cross_platform?.platforms
  if (!platforms || typeof platforms !== 'object') return '—'
  const count = Object.keys(platforms).length
  return count > 0 ? count : '—'
}

function queriesLabel(run) {
  const w = run?.total_queries_with_mention
  const t = run?.total_queries_executed
  const sm = run?.summary_metrics
  const wm = w ?? sm?.mentioned_in
  const tt = t ?? sm?.total_queries
  if (wm != null && tt != null) return `${wm}/${tt}`
  return '—'
}

function runStatusLabel(run) {
  const platforms = run?.results?.cross_platform?.platforms
  if (platforms && typeof platforms === 'object' && Object.keys(platforms).length > 0) return 'Completed'
  return 'In Progress'
}

function mentionDeltaVsNext(run, olderRun) {
  if (!olderRun) return null
  const a = Number(run?.client_mention_rate)
  const b = Number(olderRun?.client_mention_rate)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null
  const d = a - b
  return `${d >= 0 ? '+' : ''}${d.toFixed(1)}%`
}

function shareOfVoiceToBrandRanking(shareOfVoice, targetBrandName) {
  if (!Array.isArray(shareOfVoice) || shareOfVoice.length === 0) return []
  const sorted = [...shareOfVoice].sort(
    (a, b) => Number(b?.percentage ?? b?.sov ?? 0) - Number(a?.percentage ?? a?.sov ?? 0)
  )
  return sorted.map((e, i) => {
    const name = String(e?.brand ?? e?.name ?? '—').trim() || '—'
    const pct = Number(e?.percentage ?? e?.sov ?? 0)
    const isYou =
      targetBrandName &&
      name.toLowerCase() === String(targetBrandName).trim().toLowerCase()
    return {
      rank: i + 1,
      name,
      isYou,
      sov: Number.isFinite(pct) ? Math.round(pct * 10) / 10 : 0,
      queriesMentioned: e?.queries_mentioned,
      totalQueries: e?.total_queries,
      sentiment: 50,
      sentColor: '#64748b',
      change: '—',
      changeColor: '#64748b',
      barColor: BAR_COLORS[i % BAR_COLORS.length],
    }
  })
}

/** Average each brand's share-of-voice across all platforms in results.cross_platform.platforms.*.sov */
function crossPlatformToShareOfVoiceArray(crossPlatform) {
  const platforms = crossPlatform?.platforms
  if (!platforms || typeof platforms !== 'object') return []
  const totals = {}
  const counts = {}
  Object.values(platforms).forEach((p) => {
    const sov = p?.sov
    if (!sov || typeof sov !== 'object') return
    Object.entries(sov).forEach(([brand, val]) => {
      const n = Number(val)
      if (!Number.isFinite(n)) return
      totals[brand] = (totals[brand] ?? 0) + n
      counts[brand] = (counts[brand] ?? 0) + 1
    })
  })
  return Object.keys(totals).map((brand) => ({
    brand,
    sov: counts[brand] ? totals[brand] / counts[brand] : 0,
  }))
}

const SCHEDULE_FREQ_MINUTES = {
  daily: 24 * 60,
  weekly: 7 * 24 * 60,
  monthly: 30 * 24 * 60,
}

function intervalMinutesToFrequency(mins) {
  if (mins == null || !Number.isFinite(Number(mins))) return 'weekly'
  const n = Number(mins)
  if (n === SCHEDULE_FREQ_MINUTES.daily) return 'daily'
  if (n === SCHEDULE_FREQ_MINUTES.weekly) return 'weekly'
  if (n === SCHEDULE_FREQ_MINUTES.monthly) return 'monthly'
  return 'weekly'
}

function formatScheduleLabel(intervalMinutes) {
  if (intervalMinutes == null || !Number.isFinite(Number(intervalMinutes))) return null
  const n = Number(intervalMinutes)
  if (n < 60) return null
  if (n === SCHEDULE_FREQ_MINUTES.daily) return 'Daily'
  if (n === SCHEDULE_FREQ_MINUTES.weekly) return 'Weekly'
  if (n === SCHEDULE_FREQ_MINUTES.monthly) return 'Monthly'
  if (n % 1440 === 0) return `Every ${n / 1440} day(s)`
  return `Every ${n} min`
}

function SimulationRunTab({
  recentSimulationId = null,
  recentSimulationDetails = null,
  selectedRun = null,
  onRunSelected = () => {},
  onReturnToLatestRun = () => {},
  onNewRun = () => {},
  onGoToDownload = null,
  translationViewLang = 'or',
}) {
  const context = useSelector((state) => state.context)
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [showNewRunModal, setShowNewRunModal] = useState(false)
  const [schedForm, setSchedForm] = useState({ frequency: 'weekly' })
  /** Persisted schedule interval for UI when details API does not return interval_minutes */
  const [scheduleIntervalHint, setScheduleIntervalHint] = useState(null)
  const [runs, setRuns] = useState([])
  const [runsLoading, setRunsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState(emptyPagination)
  const [rerunLoading, setRerunLoading] = useState(false)
  const [compareData, setCompareData] = useState(null)
  const [compareLoading, setCompareLoading] = useState(false)
  const [hoveredPoint, setHoveredPoint] = useState(null)
  const [selectedRunLoading, setSelectedRunLoading] = useState(false)
  const [selectedRunPendingId, setSelectedRunPendingId] = useState(null)

  const scheduledIntervalMinutes =
    recentSimulationDetails?.interval_minutes ?? scheduleIntervalHint
  const hasScheduledRuns =
    scheduledIntervalMinutes != null && Number(scheduledIntervalMinutes) >= 60
  const scheduleLabel = formatScheduleLabel(scheduledIntervalMinutes)

  const openScheduleModal = () => {
    const freq = hasScheduledRuns
      ? intervalMinutesToFrequency(scheduledIntervalMinutes)
      : 'weekly'
    setSchedForm({ frequency: freq })
    setShowScheduleModal(true)
  }

  useEffect(() => {
    setPage(1)
  }, [recentSimulationId])

  useEffect(() => {
    setScheduleIntervalHint(null)
  }, [recentSimulationId])

  useEffect(() => {
    setCompareData(null)
  }, [recentSimulationId])

  //fetch all runs
  const fetchRuns = useCallback(
    async (pageNum = 1) => {
      if (recentSimulationId == null || recentSimulationId === '') {
        setRuns([])
        setPagination(emptyPagination)
        return
      }
      setRunsLoading(true)
      try {
        const res = await getAllRuns({
          simulation_id: recentSimulationId,
          page: pageNum,
          limit: PER_PAGE,
        })
        if (res?.status < 400) {
          const body = res?.data ?? res
          const list = Array.isArray(body?.data) ? body.data : []
          setRuns(list)
          const pag = body?.pagination
          if (pag && typeof pag === 'object') {
            setPagination({
              total: pag.total ?? 0,
              totalPages: pag.totalPages ?? 0,
              currentPage: pag.currentPage ?? pageNum,
              perPage: pag.perPage ?? PER_PAGE,
              hasNextPage: Boolean(pag.hasNextPage),
              hasPrevPage: Boolean(pag.hasPrevPage),
              nextPage: pag.nextPage ?? null,
              prevPage: pag.prevPage ?? null,
            })
          } else {
            setPagination((prev) => ({ ...prev, currentPage: pageNum, total: list.length, totalPages: 1 }))
          }
        } else {
          setRuns([])
          notifyError(res?.data?.message ?? 'Failed to load runs')
        }
      } catch (error) {
        console.error('Failed to fetch all runs', error)
        notifyError(error?.response?.data?.message || 'Failed to fetch all runs')
        setRuns([])
      } finally {
        setRunsLoading(false)
      }
    },
    [recentSimulationId]
  )

  useEffect(() => {
    if (recentSimulationId == null || recentSimulationId === '') {
      setRuns([])
      setPagination(emptyPagination)
      return
    }
    fetchRuns(page)
  }, [recentSimulationId, page, fetchRuns, recentSimulationDetails?.updatedAt])

  //fetch compares
  const fetchCompares = useCallback(async () => {
    if (recentSimulationId == null || recentSimulationId === '') {
      setCompareData(null)
      return
    }

    setCompareLoading(true)
    try {
      const res = await compareSimulationOverTime(recentSimulationId)
      if (res?.status < 400) {
        const body = res?.data?.data ?? res?.data
        if (body && typeof body === 'object') {
          setCompareData(body)
        } else {
          setCompareData(null)
        }
      } else {
        setCompareData(null)
        notifyError(res?.data?.message ?? 'Failed to load comparison')
      }
    } catch (error) {
      console.error('Failed to fetch compares', error)
      setCompareData(null)
      notifyError(error?.response?.data?.message ?? 'Failed to fetch data')
    } finally {
      setCompareLoading(false)
    }
  }, [recentSimulationId])

  useEffect(() => {
    fetchCompares()
  }, [fetchCompares])

  const latestRun = runs[0] ?? null
  const hasPendingRun = page === 1 && latestRun != null && runStatusLabel(latestRun) !== 'Completed'
  // The run shown by default (headline KPIs, "return to latest") — the newest run that
  // actually has results. A still-processing run has none yet, so it's skipped here even
  // though it's `latestRun` (it's still listed, just not clickable, in the table below).
  const latestCompletedRun = runs.find((r) => runStatusLabel(r) === 'Completed') ?? null

  //fetch a single run's full details when a run-history row is clicked
  const handleSelectRun = useCallback(
    async (runId) => {
      if (selectedRunLoading || runId == null) return
      const currentlyDisplayedId = selectedRun?.id ?? latestCompletedRun?.id
      if (currentlyDisplayedId === runId) return
      // Clicking the run that's already shown by default should clear any selection rather
      // than store a redundant one — otherwise every other tab would show the "viewing an
      // old run" badge for the run they're already looking at.
      if (runId === latestCompletedRun?.id) {
        onReturnToLatestRun()
        return
      }
      setSelectedRunLoading(true)
      setSelectedRunPendingId(runId)
      try {
        const res = await getRunById(runId, translationViewLang === 'en')
        if (res?.status < 400) {
          const body = res?.data?.data ?? res?.data
          onRunSelected(body ?? null)
        } else {
          notifyError(res?.data?.message ?? 'Failed to load run details')
        }
      } catch (error) {
        console.error('Failed to fetch run details', error)
        notifyError(error?.response?.data?.message ?? 'Failed to fetch run details')
      } finally {
        setSelectedRunLoading(false)
        setSelectedRunPendingId(null)
      }
    },
    [selectedRunLoading, selectedRun, latestCompletedRun, onRunSelected, onReturnToLatestRun, translationViewLang]
  )

  const handleReturnToLatest = () => {
    onReturnToLatestRun()
    // latestRun is derived from runs[0], which only reflects the true latest run on page 1
    if (page !== 1) setPage(1)
  }

  //handle new run
  const handleNewRun = async () => {
    if (context?.simulation_id == null || context?.simulation_id === '' || rerunLoading) return
    if (hasPendingRun) {
      notifyError(
        'A run is already in progress for this simulation. Please wait for it to finish before starting a new one.'
      )
      setShowNewRunModal(false)
      return
    }
    setRerunLoading(true)
    try {
      const res = await triggerReRun(Number(context?.simulation_id))
      if (res?.status < 400) {
        notifySuccess(res?.data?.message ?? 'Simulation re-run started')
        setPage(1)
        await fetchRuns(1)
        await fetchCompares()
        onNewRun?.()
      } else {
        notifyError(res?.data?.message ?? 'Failed to start re-run')
      }
    } catch (e) {
      notifyError(e?.response?.data?.message ?? 'Failed to start re-run')
    } finally {
      setRerunLoading(false)
      setShowNewRunModal(false)
    }
  }


  //schedule run
  const handleScheduleRun = async (intervalMinutes) => {
    if (context?.simulation_id == null || context?.simulation_id === '') return

    const payload = {
      simulation_id: Number(context.simulation_id),
      interval_minutes: intervalMinutes || 30 * 24 * 60,
    }
    try {
      const res = await scheduleRun(payload)
      if (res?.status < 400) {
        notifySuccess(res?.data?.message ?? 'Simulation scheduled successfully')
        setScheduleIntervalHint(intervalMinutes)
        setShowScheduleModal(false)
        onNewRun?.()
      } else {
        notifyError(res?.data?.message ?? 'Failed to schedule run')
      }
    } catch (error) {
      console.error('Failed to schedule run', error)
      notifyError(error?.response?.data?.message ?? 'Failed to schedule run')
    }
  }

  const handleRemoveSchedule = async () => {
    try {
      const res = await removeSchedule(Number(context?.simulation_id))
      if (res?.status < 400) {
        notifySuccess(res?.data?.message ?? 'Simulation schedule removed successfully')
        setScheduleIntervalHint(null)
        setShowScheduleModal(false)
        onNewRun?.()
      } else {
        notifyError(res?.data?.message ?? 'Failed to remove schedule')
      }
    } catch (error) {
      console.error('Failed to remove schedule', error)
      notifyError(error?.response?.data?.message ?? 'Failed to remove schedule')
    }
  }

  const saveSchedule = async () => {
    const intervalMinutes = SCHEDULE_FREQ_MINUTES[schedForm.frequency]
    if (intervalMinutes == null) return
    await handleScheduleRun(intervalMinutes)
  }

  const goToPage = (p) => {
    if (p < 1) return
    if (pagination.totalPages > 0 && p > pagination.totalPages) return
    setPage(p)
  }

  const latestCompletedIndexOnPage = latestCompletedRun
    ? runs.findIndex((r) => r.id === latestCompletedRun.id)
    : -1
  const prevRun = latestCompletedIndexOnPage >= 0 ? runs[latestCompletedIndexOnPage + 1] ?? null : null
  const mentionDelta =
    latestCompletedRun != null && prevRun != null
      ? Number(latestCompletedRun.client_mention_rate) - Number(prevRun.client_mention_rate)
      : 0
  const completedCount = runs.filter((r) => runStatusLabel(r) === 'Completed').length
  const totalRunsCount = pagination.total ?? runs.length

  const useCompareStats =
    compareData != null &&
    String(compareData.simulation_id ?? '') === String(recentSimulationId ?? '')

  /** When a specific historical run is selected, headline KPIs show that run's own numbers instead of latest/aggregate */
  const isViewingSelectedRun = selectedRun != null
  const displayRun = selectedRun ?? latestCompletedRun
  const effectiveUseCompareStats = useCompareStats && !isViewingSelectedRun
  const selectedRunIndexOnPage = isViewingSelectedRun
    ? runs.findIndex((r) => r.id === selectedRun.id)
    : -1
  const selectedRunOlder =
    selectedRunIndexOnPage >= 0 ? runs[selectedRunIndexOnPage + 1] ?? null : null
  const selectedRunDelta =
    isViewingSelectedRun && selectedRunOlder && selectedRun.best_mention_rate != null
      ? Number(selectedRun.best_mention_rate) - Number(selectedRunOlder.client_mention_rate)
      : null

  const avgDuration = useMemo(() => {
    if (!runs.length) return '—'
    let totalMs = 0
    let n = 0
    runs.forEach((r) => {
      if (runStatusLabel(r) !== 'Completed') return
      if (!r?.createdAt || !r?.updatedAt) return
      const ms = new Date(r.updatedAt) - new Date(r.createdAt)
      if (Number.isFinite(ms) && ms >= 0) {
        totalMs += ms
        n += 1
      }
    })
    if (!n) return '—'
    const avg = totalMs / n
    const s = Math.floor(avg / 1000)
    const m = Math.floor(s / 60)
    const rs = s % 60
    return m > 0 ? `${m}m ${rs}s` : `${rs}s`
  }, [runs])

  const targetBrandName = recentSimulationDetails?.project?.brand?.name ?? displayRun?.extracted_insights?.brand_name ?? ''

  const brandRanking = useMemo(() => {
    if (
      effectiveUseCompareStats &&
      Array.isArray(compareData.shareOfVoice) &&
      compareData.shareOfVoice.length > 0
    ) {
      return shareOfVoiceToBrandRanking(compareData.shareOfVoice, targetBrandName)
    }
    if (displayRun?.extracted_insights?.share_of_voice) {
      return shareOfVoiceToBrandRanking(displayRun.extracted_insights.share_of_voice, targetBrandName)
    }
    const cpSov = crossPlatformToShareOfVoiceArray(displayRun?.results?.cross_platform)
    if (cpSov.length > 0) return shareOfVoiceToBrandRanking(cpSov, targetBrandName)
    return []
  }, [effectiveUseCompareStats, compareData, displayRun, targetBrandName])

  const sparkPoints = useMemo(() => {
    if (
      useCompareStats &&
      Array.isArray(compareData.mentionRateOverTime) &&
      compareData.mentionRateOverTime.length > 0
    ) {
      const sorted = [...compareData.mentionRateOverTime].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      )
      return sorted.map((row) => {
        const v = Number(row?.mentionRate ?? 0)
        return Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : 0
      })
    }
    if (!runs.length) return []
    const chronological = [...runs].reverse()
    return chronological.map((r) => {
      const v = Number(r?.client_mention_rate ?? r?.summary_metrics?.visibility_rate ?? 0)
      return Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : 0
    })
  }, [useCompareStats, compareData, runs])

  const sparkLabels = useMemo(() => {
    if (
      useCompareStats &&
      Array.isArray(compareData.mentionRateOverTime) &&
      compareData.mentionRateOverTime.length > 0
    ) {
      const sorted = [...compareData.mentionRateOverTime].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      )
      return sorted.map((row) => formatRunDate(row?.date).split(',')[0] || '—')
    }
    if (!runs.length) return []
    const chronological = [...runs].reverse()
    return chronological.map((r) => formatRunDate(r?.createdAt).split(',')[0] || '—')
  }, [useCompareStats, compareData, runs])

  const kpiTotalRuns = useCompareStats
    ? (compareData.totalRuns ?? totalRunsCount)
    : totalRunsCount
  const kpiTotalRunsSub = useCompareStats
    ? 'All runs in this simulation'
    : `${completedCount} completed on this page`

  const kpiMentionValue = isViewingSelectedRun
    ? selectedRun?.best_mention_rate != null
      ? `${Number(selectedRun.best_mention_rate).toFixed(1)}%`
      : '—'
    : effectiveUseCompareStats && compareData.latestMentionRate != null
      ? `${Number(compareData.latestMentionRate).toFixed(1)}%`
      : latestCompletedRun?.client_mention_rate != null
        ? `${Number(latestCompletedRun.client_mention_rate).toFixed(1)}%`
        : '—'

  const kpiMentionPlatform = isViewingSelectedRun
    ? platformLabel(selectedRun?.mention_rate_platform)
    : effectiveUseCompareStats
      ? platformLabel(compareData.latestMentionRatePlatform)
      : platformLabel(latestCompletedRun?.mention_rate_platform)

  const kpiMentionSub = (() => {
    if (isViewingSelectedRun) {
      if (selectedRunOlder && selectedRunDelta != null && Number.isFinite(selectedRunDelta)) {
        return `${selectedRunDelta >= 0 ? '↗' : '↘'} ${Math.abs(selectedRunDelta).toFixed(1)}% vs next run`
      }
      return `Run #${selectedRun.id} · ${formatRunDate(selectedRun.createdAt)}`
    }
    if (effectiveUseCompareStats) {
      const d = compareData.mentionRateDeltaVsPrevious
      if (
        compareData.previousMentionRate != null &&
        d != null &&
        Number.isFinite(Number(d))
      ) {
        const n = Number(d)
        return `${n >= 0 ? '↗' : '↘'} ${Math.abs(n).toFixed(1)}% vs previous run`
      }
      return 'Not enough runs yet to compare'
    }
    return prevRun
      ? `${mentionDelta >= 0 ? '↗' : '↘'} ${Math.abs(mentionDelta).toFixed(1)}% vs next run on page`
      : 'Most recent run on this page'
  })()

  const kpiMentionTrend = (() => {
    if (isViewingSelectedRun) {
      return selectedRunDelta != null ? (selectedRunDelta >= 0 ? 'up' : 'down') : 'up'
    }
    if (
      effectiveUseCompareStats &&
      compareData.mentionRateDeltaVsPrevious != null &&
      Number.isFinite(Number(compareData.mentionRateDeltaVsPrevious))
    ) {
      return Number(compareData.mentionRateDeltaVsPrevious) >= 0 ? 'up' : 'down'
    }
    return mentionDelta >= 0 ? 'up' : 'down'
  })()

  const kpiAvgPosValue = isViewingSelectedRun
    ? selectedRun?.best_client_avg_position != null
      ? `#${selectedRun.best_client_avg_position}`
      : '—'
    : effectiveUseCompareStats && compareData.latestClientAvgPosition != null
      ? `#${Number(compareData.latestClientAvgPosition).toFixed(2)}`
      : latestCompletedRun?.client_avg_position != null
        ? `#${latestCompletedRun.client_avg_position}`
        : '—'

  const kpiAvgPosSub = isViewingSelectedRun
    ? selectedRun?.best_client_share_of_voice != null
      ? `Client SOV ${Number(selectedRun.best_client_share_of_voice).toFixed(1)}%`
      : `Run #${selectedRun.id}`
    : effectiveUseCompareStats
      ? [
          compareData.latestClientShareOfVoice != null
            ? `Client SOV ${Number(compareData.latestClientShareOfVoice).toFixed(1)}%`
            : null,
          compareData.latestSovRank != null ? `SOV rank #${compareData.latestSovRank}` : null,
        ]
          .filter(Boolean)
          .join(' · ') || 'Latest run (simulation-wide)'
      : 'From latest run on this page'

  const kpiDurationValue = isViewingSelectedRun
    ? formatDuration(selectedRun?.createdAt, selectedRun?.updatedAt)
    : effectiveUseCompareStats && compareData.averageDurationMsPerRun != null
      ? formatDurationMs(compareData.averageDurationMsPerRun)
      : avgDuration
  const kpiDurationSub = isViewingSelectedRun
    ? 'Duration of this run'
    : effectiveUseCompareStats
      ? 'Average per run (simulation)'
      : 'per run (this page)'

  const compareMentionDelta =
    useCompareStats &&
    compareData.mentionRateDeltaVsPrevious != null &&
    Number.isFinite(Number(compareData.mentionRateDeltaVsPrevious))
      ? Number(compareData.mentionRateDeltaVsPrevious)
      : null

  const noSimulation = recentSimulationId == null || recentSimulationId === ''
  const totalRunsForTrend = Number(kpiTotalRuns)
  const mentionRateOverTimeNeedsMultipleRuns =
    !noSimulation && Number.isFinite(totalRunsForTrend) && totalRunsForTrend === 1

  const getPointX = useCallback(
    (i) => (sparkPoints.length <= 1 ? 150 : 3 + (i / (sparkPoints.length - 1)) * 294),
    [sparkPoints]
  )

  const getPointY = useCallback((val) => 157 - (val / 100) * 154, [])

  return (
    <div className={styles.runsTab}>
      <div className={styles.runsTabHeader}>
        <div>
          <h2 className={styles.runsTabTitle}>
            Simulation Runs ({noSimulation ? 0 : totalRunsCount})
          </h2>
          {scheduleLabel && (
            <p className={styles.runsTabScheduleLabel}>
              <Clock size={12} />
              Scheduled: {scheduleLabel}
            </p>
          )}
          {isViewingSelectedRun &&
            latestCompletedRun &&
            selectedRun.id !== latestCompletedRun.id && (
            <button
              type="button"
              onClick={handleReturnToLatest}
              className={styles.runsTabViewingBadge}
              title="Return to the most recent run"
            >
              <ArrowLeft size={12} />
              Viewing Run #{selectedRun.id} ({formatRunDate(selectedRun.createdAt).split(',')[0]})
            </button>
          )}
        </div>
        <div className={styles.runsTabActions}>
          <button
            type="button"
            onClick={openScheduleModal}
            className={`${styles.runsTabScheduleBtn} ${hasScheduledRuns ? styles.active : ''}`}
          >
            {hasScheduledRuns && <span className={styles.runsTabScheduleDot} />}
            <Calendar size={16} />
          </button>
          <button
            type="button"
            onClick={() => {
              if (hasPendingRun) {
                notifyError(
                  'A run is already in progress for this simulation. Please wait for it to finish before starting a new one.'
                )
                return
              }
              setShowNewRunModal(true)
            }}
            disabled={noSimulation || rerunLoading}
            className={styles.runsTabNewRunBtn}
            style={{
              opacity: noSimulation || rerunLoading || hasPendingRun ? 0.6 : 1,
              cursor: noSimulation || rerunLoading || hasPendingRun ? 'not-allowed' : 'pointer',
            }}
            title={hasPendingRun ? 'A run is already in progress for this simulation' : undefined}
          >
            <Play size={14} />
            {hasPendingRun ? 'New Run' : rerunLoading ? 'Starting…' : 'New Run'}
          </button>
        </div>
      </div>

      {noSimulation ? (
        <Card>
          <p style={{ color: '#64748b', fontSize: 14, padding: 8 }}>
            Open a simulation from the list to view run history.
          </p>
        </Card>
      ) : null}

      {!noSimulation && (
        <>
          <div className={styles.runsTabKpiGrid}>
            <KpiCard
              label="Total Runs"
              value={runsLoading && !effectiveUseCompareStats ? '—' : kpiTotalRuns}
              sub={kpiTotalRunsSub}
              icon={<Repeat size={20} />}
            />
            <KpiCard
              label={isViewingSelectedRun ? 'Mention Rate' : 'Latest Mention Rate'}
              value={
                kpiMentionPlatform ? (
                  <>
                    {kpiMentionValue}
                    <span
                      style={{
                        marginLeft: 6,
                        fontSize: 11,
                        fontWeight: 500,
                        color: '#64748b',
                        background: '#f1f5f9',
                        borderRadius: 4,
                        padding: '2px 6px',
                        verticalAlign: 'middle',
                      }}
                    >
                      {kpiMentionPlatform}
                    </span>
                  </>
                ) : (
                  kpiMentionValue
                )
              }
              sub={kpiMentionSub}
              trend={kpiMentionTrend}
              icon={<TrendingUp size={20} />}
            />
            <KpiCard
              label={isViewingSelectedRun ? 'Avg Position' : 'Latest Avg Position'}
              value={kpiAvgPosValue}
              sub={kpiAvgPosSub}
              trend="up"
              icon={<BarChart2 size={20} />}
            />
            <KpiCard
              label={isViewingSelectedRun ? 'Duration' : 'Avg Duration'}
              value={kpiDurationValue}
              sub={kpiDurationSub}
              icon={<Clock size={20} />}
            />
          </div>

          {effectiveUseCompareStats ? (
            <Card>
              <CardTitle sub="Latest run vs the previous run for this simulation">
                Compare runs
              </CardTitle>
              <div className={styles.runsTabCompareGrid}>
                <div className={styles.runsTabCompareCell}>
                  <div className={styles.runsTabCompareLabel}>Previous mention rate</div>
                  <div className={styles.runsTabCompareValue}>
                    {compareData.previousMentionRate != null
                      ? `${Number(compareData.previousMentionRate).toFixed(1)}%`
                      : '—'}
                  </div>
                </div>
                <div className={styles.runsTabCompareCell}>
                  <div className={styles.runsTabCompareLabel}>Latest mention rate</div>
                  <div className={styles.runsTabCompareValue}>
                    {compareData.latestMentionRate != null
                      ? `${Number(compareData.latestMentionRate).toFixed(1)}%`
                      : '—'}
                  </div>
                </div>
                <div className={styles.runsTabCompareCell}>
                  <div className={styles.runsTabCompareLabel}>Change vs previous</div>
                  <div
                    className={styles.runsTabCompareValue}
                    style={{
                      color:
                        compareMentionDelta == null
                          ? '#64748b'
                          : compareMentionDelta >= 0
                            ? '#22c55e'
                            : '#ef4444',
                    }}
                  >
                    {compareMentionDelta == null
                      ? '—'
                      : `${compareMentionDelta >= 0 ? '+' : ''}${compareMentionDelta.toFixed(1)}%`}
                  </div>
                </div>
              </div>
            </Card>
          ) : null}

          <div className={styles.runsTabChartsRow}>
            <Card>
              <CardTitle
                sub={
                  mentionRateOverTimeNeedsMultipleRuns
                    ? 'Compare mention rate across runs — needs more than one run'
                    : useCompareStats
                      ? 'Mention rate by run across the full simulation timeline'
                      : 'Track brand mention rate progression across runs on this page'
                }
              >
                Mention Rate Over Time
              </CardTitle>
              <div className={styles.runsTabChartArea}>
                {mentionRateOverTimeNeedsMultipleRuns ? (
                  <div
                    style={{
                      padding: '36px 20px',
                      textAlign: 'center',
                      color: '#64748b',
                      fontSize: 13,
                      lineHeight: 1.55,
                      maxWidth: 440,
                      margin: '0 auto',
                    }}
                  >
                    There were no multiple runs for this simulation.
                    Please create a new run to compare mention rate across runs over time.
                  </div>
                ) : sparkPoints.length > 0 ? (
                  <>
                    {[100, 75, 50, 25, 0].map((v) => (
                      <div key={v} className={styles.runsTabYLabel} style={{ top: `${100 - v}%` }}>
                        {v}
                      </div>
                    ))}
                    {[100, 75, 50, 25, 0].map((v) => (
                      <div
                        key={`g${v}`}
                        className={styles.runsTabGridLine}
                        style={{ top: `${100 - v}%` }}
                      />
                    ))}
                    <svg
                      className={styles.runsTabSparklineSvg}
                      viewBox="0 0 300 160"
                      preserveAspectRatio="none"
                    >
                      <polyline
                        points={sparkPoints
                          .map(
                            (val, i) =>
                              `${getPointX(i)},${getPointY(val)}`
                          )
                          .join(' ')}
                        fill="none"
                        stroke="#22c55e"
                        strokeWidth="2"
                        strokeDasharray="4 3"
                        opacity="0.8"
                      />
                      {sparkPoints.map((val, i) => (
                        <circle
                          key={i}
                          cx={getPointX(i)}
                          cy={getPointY(val)}
                          r="3"
                          fill="#22c55e"
                          opacity="0.9"
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={() =>
                            setHoveredPoint({
                              x: getPointX(i),
                              y: getPointY(val),
                              label: sparkLabels[i] ?? 'Run',
                              value: Number(val).toFixed(1),
                            })
                          }
                          onMouseLeave={() => setHoveredPoint(null)}
                        >
                          <title>{`${sparkLabels[i] ?? 'Run'}: ${Number(val).toFixed(1)}%`}</title>
                        </circle>
                      ))}
                    </svg>
                    {hoveredPoint ? (
                      <div
                        className={styles.runsTabPointTooltip}
                        style={{
                          left: `calc(30px + ${(hoveredPoint.x / 300) * 100}%)`,
                          top: `${(hoveredPoint.y / 160) * 100}%`,
                        }}
                      >
                        {hoveredPoint.label}: {hoveredPoint.value}%
                      </div>
                    ) : null}
                    {sparkLabels[0] && (
                      <div className={styles.runsTabXLabel} style={{ left: 30 }}>
                        {sparkLabels[0]}
                      </div>
                    )}
                    {sparkLabels.length > 1 && (
                      <div className={`${styles.runsTabXLabel} ${styles.runsTabXLabelCenter}`}>
                        {sparkLabels[Math.floor(sparkLabels.length / 2)]}
                      </div>
                    )}
                    {sparkLabels.length > 1 && (
                      <div className={styles.runsTabXLabel} style={{ right: 0, left: 'auto' }}>
                        {sparkLabels[sparkLabels.length - 1]}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ padding: 40, textAlign: 'center', color: '#999999', fontSize: 13 }}>
                    No chart data for this page.
                  </div>
                )}
              </div>
              <div className={styles.runsTabLegend}>
                {LEGEND_ITEMS.map((p) => (
                  <div key={p.label} className={styles.runsTabLegendItem}>
                    <span className={styles.runsTabLegendDot} style={{ background: p.color }} />
                    {p.label}
                  </div>
                ))}
              </div>
            </Card>

            <Card>
              <CardTitle
                sub={
                  useCompareStats
                    ? `Share of voice`
                    : 'Share of voice'
                }
              >
                Brand ranking
              </CardTitle>
              {brandRanking.length === 0 ? (
                <div style={{ padding: 24, color: '#999999', fontSize: 13 }}>No share-of-voice data.</div>
              ) : (
                <table className={styles.runsTabTable}>
                  <thead>
                    <tr>
                      <th className={styles.center}>#</th>
                      <th>Brand</th>
                      <th />
                      <th>SOV</th>
                      <th>Queries</th>
                    </tr>
                  </thead>
                  <tbody>
                    {brandRanking.map((b) => (
                      <tr key={b.rank} className={b.isYou ? styles.runsTabBrandRowYou : ''}>
                        <td style={{ textAlign: 'center' }}>
                          <span
                            className={`${styles.runsTabRankBadge} ${b.rank <= 3 ? styles.top3 : styles.other}`}
                          >
                            {b.rank}
                          </span>
                        </td>
                        <td>
                          <span
                            style={{
                              display: 'inline-block',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: b.barColor,
                              marginRight: 8,
                            }}
                          />
                          {b.name}
                          {b.isYou && (
                            <span
                              style={{
                                marginLeft: 6,
                                fontSize: 10,
                                padding: '2px 6px',
                                borderRadius: 4,
                                background: 'rgba(34,197,94,0.15)',
                                color: '#22c55e',
                                fontWeight: 600,
                              }}
                            >
                              YOU
                            </span>
                          )}
                        </td>
                        <td style={{ minWidth: 100 }}>
                          <div className={styles.runsTabSovBarWrap}>
                            <div
                              className={styles.runsTabSovBar}
                              style={{
                                width: `${Math.min(100, b.sov)}%`,
                                background: b.barColor,
                              }}
                            />
                          </div>
                        </td>
                        <td style={{ fontWeight: 600, color: '#363636' }}>{b.sov}%</td>
                        <td style={{ color: '#64748b', fontSize: 13 }}>
                          {b.queriesMentioned != null && b.totalQueries != null
                            ? `${b.queriesMentioned} / ${b.totalQueries}`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>

          <Card>
            <CardTitle sub="All simulation runs with detailed metrics">
              Run History
            </CardTitle>
            <div className={styles.runsTabRunHistoryWrap}>
              {runsLoading ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>Loading runs…</div>
              ) : runs.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#999999' }}>No runs yet.</div>
              ) : (
                <>
                  <table className={styles.runsTabTable}>
                    <thead>
                      <TableHeader
                        columns={[
                          'Run',
                          'Started',
                          'Updated',
                          'Platforms',
                          'Queries',
                          'Mention rate',
                          'SOV %',
                          'Avg pos.',
                          'Duration',
                          'Status',
                          'Δ vs next',
                        ]}
                      />
                    </thead>
                    <tbody>
                      {runs.map((r, idx) => {
                        const older = runs[idx + 1] ?? null
                        const delta = mentionDeltaVsNext(r, older)
                        const mr = r?.client_mention_rate
                        const sov = r?.client_share_of_voice
                        const pos = r?.client_avg_position
                        const isSelected = (selectedRun?.id ?? latestCompletedRun?.id) === r.id
                        const status = runStatusLabel(r)
                        const isPending = status !== 'Completed'
                        return (
                          <tr
                            key={r.id}
                            className={`${styles.runsTabRunRow} ${isSelected ? styles.runsTabRunRowSelected : ''} ${isPending ? styles.runsTabRunRowPending : ''}`}
                            onClick={() => { if (!isPending) handleSelectRun(r.id) }}
                            title={isPending ? 'Run is still processing' : 'View details for this run'}
                          >
                            <TableCell bold>
                              <span className={styles.runsTabRunId}>
                                <span className={`${styles.runsTabRunDot} ${isPending ? styles.runsTabRunDotPending : ''}`} />
                                Run #{r.id}
                                {selectedRunPendingId === r.id ? ' (loading…)' : ''}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className={styles.runsTabRunDateMain}>{formatRunDate(r.createdAt)}</div>
                            </TableCell>
                            <TableCell>
                              <div className={styles.runsTabRunDateSub}>{formatRunDate(r.updatedAt)}</div>
                            </TableCell>
                            <TableCell>{platformsFromRun(r)}</TableCell>
                            <TableCell>{queriesLabel(r)}</TableCell>
                            <TableCell>
                              <span
                                style={{
                                  color:
                                    mr == null
                                      ? '#64748b'
                                      : mr >= 85
                                        ? '#22c55e'
                                        : mr >= 50
                                          ? '#f59e0b'
                                          : '#ef4444',
                                  fontWeight: 600,
                                }}
                              >
                                {mr != null ? `${Number(mr).toFixed(1)}%` : '—'}
                              </span>
                              {mr != null && r?.mention_rate_platform ? (
                                <span
                                  style={{
                                    marginLeft: 6,
                                    fontSize: 11,
                                    fontWeight: 500,
                                    color: '#64748b',
                                    background: '#f1f5f9',
                                    borderRadius: 4,
                                    padding: '2px 6px',
                                  }}
                                >
                                  {platformLabel(r.mention_rate_platform)}
                                </span>
                              ) : null}
                            </TableCell>
                            <TableCell>{sov != null ? `${Number(sov).toFixed(1)}%` : '—'}</TableCell>
                            <TableCell>{pos != null ? `#${pos}` : '—'}</TableCell>
                            <TableCell>{formatDuration(r.createdAt, r.updatedAt)}</TableCell>
                            <TableCell>
                              <Badge color={isPending ? '#f59e0b' : '#22c55e'}>{status}</Badge>
                            </TableCell>
                            <TableCell
                              style={{
                                color: delta?.startsWith('-') ? '#ef4444' : '#22c55e',
                                fontWeight: 500,
                              }}
                            >
                              {delta ? `${delta.startsWith('-') ? '↘' : '↗'} ${delta}` : '–'}
                            </TableCell>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                  {pagination.totalPages > 0 && pagination.total > 0 && (
                    <div className={styles.pagination}>
                      <span className={styles.paginationInfo}>
                        Showing{' '}
                        {pagination.total === 0
                          ? 0
                          : (pagination.currentPage - 1) * pagination.perPage + 1}
                        –
                        {Math.min(pagination.currentPage * pagination.perPage, pagination.total)} of{' '}
                        {pagination.total}
                      </span>
                      <div className={styles.paginationBtns}>
                        <button
                          type="button"
                          onClick={() => goToPage(pagination.prevPage ?? pagination.currentPage - 1)}
                          disabled={!pagination.hasPrevPage}
                          className={styles.pageBtn}
                          title="Previous page"
                        >
                          <ChevronLeft size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => goToPage(pagination.nextPage ?? pagination.currentPage + 1)}
                          disabled={!pagination.hasNextPage}
                          className={styles.pageBtn}
                          title="Next page"
                        >
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>
        </>
      )}

      {!noSimulation && typeof onGoToDownload === 'function' && (
        <div className={styles.personaActions}>
          <NextButton label="Download Results" onClick={onGoToDownload} icon = {<Download size={16} />} />
        </div>
      )}

      <ScheduleModal
        open={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        schedForm={schedForm}
        setSchedForm={setSchedForm}
        onSave={saveSchedule}
        onRemoveSchedule={handleRemoveSchedule}
        hasExistingSchedule={hasScheduledRuns}
      />
      <NewRunModal
        open={showNewRunModal}
        onClose={() => setShowNewRunModal(false)}
        onConfirm={handleNewRun}
        loading={rerunLoading}
      />
    </div>
  )
}

export default SimulationRunTab
